import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import { useTranslation } from '../LanguageContext';
import { ReputationBadge } from './ReputationBadge';
import { AvailabilityChip } from './AvailabilityPanel';

/**
 * The showcase, pointed the other way.
 *
 * Brands had a campaign vitrin; creators had nothing that put them in front of
 * a brand which had not already gone looking. `CreatorDiscovery` is the browse
 * surface, `CampaignSuggestions` is the same ranking aimed at one brief.
 *
 * Both print **why** each result is there. A recommendation nobody can audit
 * is one nobody acts on — and one we could not debug either. `over_budget` is
 * shown as loudly as a match, because "this creator's floor is above your
 * budget" saves a brand a wasted conversation.
 */

const usd = (value) => `$${Number(value || 0).toLocaleString('en-US', {
    minimumFractionDigits: 0, maximumFractionDigits: 0,
})}`;

const compact = (value) => {
    const n = Number(value || 0);
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
    return String(n);
};

const PLATFORMS = ['instagram', 'tiktok', 'youtube', 'x', 'facebook'];

/** One ranking signal, said out loud. `over_budget` is a warning, not a match. */
function Reason({ reason, t }) {
    const bad = reason.key === 'over_budget';
    return (
        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold border ${bad
            ? 'bg-red-50 text-red-600 border-red-200'
            : 'bg-slate-50 text-slate-600 border-slate-200'}`}>
            {t(`discover.reason_${reason.key}`)}
            {reason.value && <span className="font-bold text-slate-700">{reason.value}</span>}
        </span>
    );
}

export function CreatorCard({ creator, t, onInvite }) {
    return (
        <div className="rounded-2xl bg-white border border-slate-200 p-3.5 hover:shadow-md transition">
            <div className="flex items-start gap-3">
                <Link to={`/${creator.username}`} className="flex-shrink-0">
                    <img src={creator.profile_picture || '/logo192.png'} alt=""
                        className="w-11 h-11 rounded-full object-cover ring-2 ring-slate-100" />
                </Link>

                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                        <Link to={`/${creator.username}`}
                            className="text-sm font-bold text-slate-800 truncate no-underline hover:underline">
                            {creator.username}
                        </Link>
                        {creator.verified && (
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 text-primary-600 flex-shrink-0"
                                fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                        )}
                        <ReputationBadge rating={creator.rating} compact />
                        <AvailabilityChip availability={creator.availability} compact />
                    </div>

                    {creator.profile_text && (
                        <p className="text-[11px] text-slate-500 line-clamp-2 leading-relaxed mt-0.5">
                            {creator.profile_text}
                        </p>
                    )}

                    {/* Numbers first — this is what a brand is scanning for. */}
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1.5 text-[11px] text-slate-500">
                        <span><strong className="text-slate-700 tabular-nums">{compact(creator.followers)}</strong> {t('campaigns.followers')}</span>
                        {creator.avg_reach > 0 && (
                            <span><strong className="text-slate-700 tabular-nums">{compact(creator.avg_reach)}</strong> {t('discover.reach')}</span>
                        )}
                        {creator.engagement_rate > 0 && (
                            <span><strong className="text-slate-700 tabular-nums">%{creator.engagement_rate}</strong> {t('discover.engagement')}</span>
                        )}
                        {creator.starting_usd !== null && creator.starting_usd !== undefined && (
                            <span className="text-primary-700 font-bold tabular-nums">
                                {t('discover.from')} {usd(creator.starting_usd)}
                            </span>
                        )}
                    </div>
                </div>

                <div className="text-right flex-shrink-0">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide leading-none">
                        {t('discover.match')}
                    </p>
                    <p className="text-lg font-black text-primary-700 tabular-nums leading-tight">
                        {Math.round(creator.match_score)}
                    </p>
                </div>
            </div>

            {creator.reasons?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2.5">
                    {creator.reasons.map((reason, index) => (
                        <Reason key={index} reason={reason} t={t} />
                    ))}
                </div>
            )}

            <div className="flex gap-2 mt-3">
                <Link to={`/${creator.username}/media-kit`}
                    className="btn-secondary flex-1 py-2 text-xs text-center no-underline">
                    {t('discover.viewKit')}
                </Link>
                {/* Reaching out is a DM, and the profile is where that lives —
                    a second, parallel contact flow would just be one more
                    place for the inbox gates to be forgotten. */}
                <Link to={`/${creator.username}`}
                    className="btn-primary flex-1 py-2 text-xs text-center no-underline"
                    onClick={onInvite}>
                    {t('discover.contact')}
                </Link>
            </div>
        </div>
    );
}

/** Suggestions for one campaign — collapsed until asked for, because it is a
 *  scan over every creator and nobody wants it on page load. */
export function CampaignSuggestions({ campaignId }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = async () => {
        if (data || loading) return;
        setLoading(true);
        try {
            const res = await api.get(`/api/campaigns/${campaignId}/suggestions/`);
            setData(res.data);
        } catch (err) { /* the interceptor surfaces auth/payment errors */ }
        finally { setLoading(false); }
    };

    return (
        <div className="rounded-xl border border-primary-200 bg-primary-50/40 overflow-hidden">
            <button onClick={() => { setOpen(!open); if (!open) load(); }}
                className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-primary-50 transition">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-primary-600 flex-shrink-0"
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
                <div className="min-w-0 flex-1">
                    <p className="text-xs font-bold text-slate-800">{t('discover.suggestTitle')}</p>
                    <p className="text-[10px] text-slate-500 truncate">{t('discover.suggestHint')}</p>
                </div>
                {data && (
                    <span className="px-1.5 py-0.5 rounded-full bg-primary-600 text-white text-[10px] font-bold flex-shrink-0">
                        {data.results.length}
                    </span>
                )}
                <svg xmlns="http://www.w3.org/2000/svg"
                    className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
            </button>

            {open && (
                <div className="px-3 pb-3 pt-1 space-y-2 border-t border-primary-100">
                    {loading && <div className="flex justify-center py-4"><span className="spinner-sm" /></div>}
                    {data && data.results.length === 0 && (
                        <p className="text-xs text-slate-500 py-3 text-center leading-relaxed">
                            {t('discover.suggestEmpty')}
                        </p>
                    )}
                    {data?.results.map(creator => (
                        <CreatorCard key={creator.id} creator={creator} t={t} />
                    ))}
                </div>
            )}
        </div>
    );
}

function CreatorDiscovery() {
    const { t } = useTranslation();
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState('');
    const [platform, setPlatform] = useState('');
    const [budget, setBudget] = useState('');
    const [openOnly, setOpenOnly] = useState(false);
    const [minFollowers, setMinFollowers] = useState('');

    const load = useCallback(async () => {
        try {
            const params = {};
            if (query) params.q = query;
            if (platform) params.platform = platform;
            if (budget) params.budget = budget;
            if (minFollowers) params.min_followers = minFollowers;
            if (openOnly) params.open_to_work = 'true';

            const res = await api.get('/api/discover/creators/', { params });
            setResults(res.data.results || []);
        } catch (err) { /* the interceptor surfaces auth/payment errors */ }
        finally { setLoading(false); }
    }, [query, platform, budget, minFollowers, openOnly]);

    useEffect(() => {
        const timer = setTimeout(load, query ? 350 : 0);   // debounce the search box
        return () => clearTimeout(timer);
    }, [load, query]);

    return (
        <div className="space-y-3">
            <div className="rounded-2xl bg-white border border-slate-200 p-3 space-y-2">
                <input className="input py-2 text-sm" value={query}
                    placeholder={t('discover.searchPlaceholder')}
                    onChange={(e) => setQuery(e.target.value)} />

                <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-0.5">
                    <select className="input py-1.5 text-xs w-auto flex-shrink-0" value={platform}
                        onChange={(e) => setPlatform(e.target.value)}>
                        <option value="">{t('campaigns.allPlatforms')}</option>
                        {PLATFORMS.map(p => (
                            <option key={p} value={p} className="capitalize">{p}</option>
                        ))}
                    </select>

                    <div className="relative flex-shrink-0 w-28">
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400 pointer-events-none">$</span>
                        <input type="number" min={0} className="input py-1.5 pl-6 text-xs w-full"
                            placeholder={t('discover.budget')} value={budget}
                            onChange={(e) => setBudget(e.target.value)} />
                    </div>

                    <input type="number" min={0} className="input py-1.5 text-xs w-32 flex-shrink-0"
                        placeholder={t('search.minFollowers')} value={minFollowers}
                        onChange={(e) => setMinFollowers(e.target.value)} />

                    <button onClick={() => setOpenOnly(!openOnly)}
                        className={`px-3 py-1.5 rounded-xl text-xs font-semibold border flex-shrink-0 transition whitespace-nowrap ${openOnly
                            ? 'bg-emerald-600 text-white border-emerald-600'
                            : 'bg-white text-slate-500 border-slate-200'}`}>
                        {t('availability.openToWork')}
                    </button>
                </div>

                {budget && (
                    <p className="text-[11px] text-slate-400 leading-relaxed">
                        {t('discover.budgetHint')}
                    </p>
                )}
            </div>

            {loading ? (
                <div className="flex justify-center py-16"><div className="spinner" /></div>
            ) : results.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                    <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center text-slate-300 mb-3">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8" fill="none"
                            viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                    </div>
                    <p className="text-sm font-bold text-slate-600">{t('discover.empty')}</p>
                    <p className="text-xs text-slate-400 mt-1 max-w-xs leading-relaxed">{t('discover.emptyDesc')}</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    {results.map(creator => (
                        <CreatorCard key={creator.id} creator={creator} t={t} />
                    ))}
                </div>
            )}
        </div>
    );
}

export default CreatorDiscovery;
